import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { formatScore, formatWeight, scoreRisk, type RiskAnswer } from '@norvix/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { riskScoringApi, type RiskAssessmentRecord } from '../api/risk-scoring.api.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * A client's weighted risk assessment (DEV-90): the latest total and how it was
 * reached, every earlier one, and the form an officer scores the client with.
 *
 * The total and classification shown while filling in come from the same
 * arithmetic the gateway records, so what the officer sees is what is kept.
 */

function Breakdown({ record }: { readonly record: RiskAssessmentRecord }): JSX.Element {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={`Breakdown of the assessment of ${new Date(record.assessedAt).toLocaleDateString(APP_LOCALE)}`}>
        <TableHead><TableRow><TableCell>Criterion</TableCell><TableCell>Weight</TableCell><TableCell>Level</TableCell><TableCell align="right">Score</TableCell><TableCell>Comment</TableCell></TableRow></TableHead>
        <TableBody>
          {record.categories.map((category) => [
            <TableRow key={category.categoryId} sx={{ '& td': { fontWeight: 600 } }}>
              <TableCell>{category.name}</TableCell><TableCell>{formatWeight(category.weightBp)}</TableCell><TableCell />
              <TableCell align="right">{formatScore(category.subtotalHundredths)}</TableCell><TableCell />
            </TableRow>,
            ...category.criteria.map((criterion) => (
              <TableRow key={criterion.criterionId}>
                <TableCell sx={{ pl: 4 }}>{criterion.name}</TableCell><TableCell>{formatWeight(criterion.weightBp)}</TableCell>
                <TableCell>{criterion.levelLabel}</TableCell><TableCell align="right">{String(criterion.score)}</TableCell>
                <TableCell sx={{ maxWidth: 280, overflowWrap: 'anywhere' }}>{criterion.comment}</TableCell>
              </TableRow>
            )),
          ])}
        </TableBody>
      </Table>
    </Box>
  );
}

export function RiskScoringPanel({ customerId }: { readonly customerId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['risk-scoring', customerId],
    queryFn: () => riskScoringApi.forCustomer(customerId),
  });
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, RiskAnswer>>({});
  const [comment, setComment] = useState('');
  const [apply, setApply] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isLoading) return <Paper sx={{ p: 3 }}><CircularProgress size={20} aria-label="Loading risk assessments" /></Paper>;
  // A response of the wrong shape is a failed load, not a reason to take the
  // whole client card down with it.
  if (error !== null || data === undefined || !Array.isArray(data.assessments)) {
    return <Paper sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>Risk assessments could not be loaded.</Alert></Paper>;
  }

  const model = data.model;
  const latest = data.assessments[0];
  const live = model === null ? null : scoreRisk(model.definition, Object.values(answers));
  const setAnswer = (criterionId: string, change: Partial<RiskAnswer>): void =>
    setAnswers((prev) => ({ ...prev, [criterionId]: { criterionId, level: prev[criterionId]?.level ?? '', ...prev[criterionId], ...change } }));

  const start = (): void => {
    // Start from the last assessment's levels and comments when the model has not changed.
    const previous = latest !== undefined && model !== null && latest.modelVersion === model.version
      ? Object.fromEntries(latest.categories.flatMap((c) => c.criteria).map((c) => [c.criterionId, { criterionId: c.criterionId, level: c.level, comment: c.comment }]))
      : {};
    setAnswers(previous);
    setComment('');
    setApply(true);
    setSaveError('');
    setOpen(true);
  };

  const save = (): void => {
    if (model === null) return;
    setSaving(true);
    setSaveError('');
    riskScoringApi.assess(customerId, { modelVersion: model.version, answers: Object.values(answers), comment, applyToRating: apply })
      .then(async () => {
        setOpen(false);
        await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: ['customer', customerId] })]);
      })
      .catch((err: unknown) => setSaveError(err instanceof Error ? err.message : 'The assessment could not be saved.'))
      .finally(() => setSaving(false));
  };

  return (
    <Paper component="section" sx={{ p: { xs: 2, sm: 3 } }} aria-labelledby="risk-scoring-title">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
        <Typography variant="h6" component="h3" id="risk-scoring-title">Weighted risk assessment</Typography>
        {model !== null ? <Button size="small" variant="outlined" onClick={start}>Score this client</Button> : null}
      </Stack>

      {model === null ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No scoring model is configured yet. It is set in <RouterLink to="/settings/compliance/risk-scoring">Settings → Risk scoring</RouterLink>.
        </Alert>
      ) : null}

      {latest === undefined ? (
        model !== null ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>This client has not been scored yet.</Typography> : null
      ) : (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="h5" component="p" aria-label="Latest total">
            Total Score {latest.total} · Client risk {latest.classification}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {new Date(latest.assessedAt).toLocaleString(APP_LOCALE)} · {latest.assessedByName ?? latest.assessedBy} · model version {String(latest.modelVersion)}
            {latest.appliedToRating ? ' · applied as the client’s rating' : ' · not applied to the rating'}
          </Typography>
          {latest.comment !== null ? <Typography variant="body2" sx={{ mt: 0.5 }}>{latest.comment}</Typography> : null}
          <Box sx={{ mt: 1.5 }}><Breakdown record={latest} /></Box>
          {data.assessments.length > 1 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Earlier assessments</Typography>
              <Stack spacing={1}>
                {data.assessments.slice(1).map((record) => (
                  <Box key={record.id}>
                    <Button size="small" onClick={() => setExpanded(expanded === record.id ? null : record.id)} aria-expanded={expanded === record.id}>
                      {new Date(record.assessedAt).toLocaleDateString(APP_LOCALE)} · {record.total} {record.classification} · {record.assessedByName ?? record.assessedBy}
                    </Button>
                    {expanded === record.id ? <Breakdown record={record} /> : null}
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Box>
      )}

      {model !== null && live !== null ? (
        <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="lg" aria-labelledby="risk-score-title">
          <DialogTitle id="risk-score-title">Score this client</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Box role="status" aria-label="Running total" sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', py: 1 }}>
                <Typography variant="h6" component="p">
                  Total Score {formatScore(live.totalHundredths)} · Client risk {live.classification ?? '—'}
                </Typography>
                {live.unanswered.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">{String(live.unanswered.length)} criteria still need a level.</Typography>
                ) : null}
              </Box>
              {live.categories.map((category) => (
                <Box key={category.categoryId}>
                  <Typography variant="subtitle1" component="h4">
                    {category.name} · {formatWeight(category.weightBp)} · subtotal {formatScore(category.subtotalHundredths)}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {category.criteria.map((criterion) => (
                      <Box key={criterion.criterionId} role="group" aria-label={criterion.name} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) 180px 70px minmax(0, 1.5fr)' }, gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2">{criterion.name} <Typography component="span" variant="caption" color="text.secondary">{formatWeight(criterion.weightBp)}</Typography></Typography>
                        <TextField select size="small" label="Risk level" value={criterion.level}
                          onChange={(event) => setAnswer(criterion.criterionId, { level: event.target.value })}>
                          {model.definition.levels.map((level) => <MenuItem key={level.level} value={level.level}>{level.label}</MenuItem>)}
                        </TextField>
                        <Typography variant="body2" align="right" aria-label="Score">{criterion.level === '' ? '—' : String(criterion.score)}</Typography>
                        <TextField size="small" label="Comment" value={answers[criterion.criterionId]?.comment ?? ''}
                          inputProps={{ maxLength: 2000 }}
                          onChange={(event) => setAnswer(criterion.criterionId, { comment: event.target.value })} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
              <TextField label="Overall comment" value={comment} onChange={(event) => setComment(event.target.value)} multiline minRows={2}
                inputProps={{ maxLength: 2000 }} helperText={apply ? 'Required when the result becomes the client’s rating.' : 'Kept with the assessment.'} />
              <FormControlLabel control={<Checkbox checked={apply} onChange={(event) => setApply(event.target.checked)} />}
                label="Apply the classification as the client’s risk rating" />
              {saveError !== '' ? <Alert severity="error">{saveError}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save}
              disabled={saving || live.classification === null || (apply && comment.trim().length < 4)}>
              {saving ? 'Saving…' : 'Record the assessment'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Paper>
  );
}
